import { cleanProductTitle } from "@/lib/productTitle";

/**
 * Supplier / dropship vendor names that must never surface to shoppers or to
 * Google Images. Keep in sync with the vendor list in `cleanProductDescription`.
 */
const VENDOR_NAMES = /\b(HomeRoots|Home Roots|HomeSullivan|Home Sullivan|Furniture of America|Baxton Studio|Modway|Flash Furniture|FurniFindUSA|FurniFind|Furnifind)\b/gi;

/** Remove any supplier name from a user-facing string. */
export function stripVendorNames(text: string): string {
  return (text || "")
    .replace(VENDOR_NAMES, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.!?])/g, "$1")
    .replace(/[\s\-–—|,]+$/, "")
    .trim();
}

/**
 * The single definition of product image alt text.
 *
 * Always derived from the cleaned display name — never from Shopify's raw
 * `altText` (which carries the supplier name) and never from the raw feed title.
 * When a gallery has several images, the view index is appended so image search
 * can tell the views apart. Pass the supplier's `productType` (or the display
 * name itself) wherever it is known, so the alt names the product as the card
 * does: "Throw Blanket", not "Blanket".
 */
export function productImageAlt(
  title: string,
  opts?: { displayName?: string; productType?: string; index?: number; total?: number; suffix?: string },
): string {
  const base = stripVendorNames(opts?.displayName || cleanProductTitle(title || "", opts?.productType)) || "Furniture";
  if (opts?.suffix) return `${base} — ${stripVendorNames(opts.suffix)}`;
  if (opts?.index != null && (opts.total ?? 0) > 1) {
    return `${base} — view ${opts.index + 1}`;
  }
  return base;
}
